/**
 * A character's voice from its seed (audio-direction §5: "each character has a consistent timbre from their
 * seed (formant set, base pitch)"): sex, base pitch, vocal-tract scale, speaking rate, breathiness, jitter,
 * intonation range, glottal tilt and a vowel preference (an accent). Pure and deterministic.
 */
import { createRng, nextFloat, type Rng } from '../../core/rng'
import type { CommsConfig } from './commsSchema'

export interface Voice {
  readonly seed: number
  readonly female: boolean
  /** Base pitch (Hz). */
  readonly pitch: number
  /** Formant scale. */
  readonly tract: number
  /** Syllables per second at stress 0. */
  readonly rate: number
  readonly breath: number
  readonly jitter: number
  readonly range: number
  readonly tilt: number
  /** Relative preference per vowel, in `Object.keys(cfg.vowels)` order. */
  readonly vowelWeights: readonly number[]
}

function inRange(rng: Rng, r: readonly [number, number]): number {
  return r[0] + (r[1] - r[0]) * nextFloat(rng)
}

/** Scrambles a character seed so neighbouring seeds sound unrelated. */
export function voiceRngSeed(seed: number): number {
  let h = (seed ^ 0x6d2b79f5) >>> 0
  h = Math.imul(h ^ (h >>> 16), 0x21f0aaad) >>> 0
  h = Math.imul(h ^ (h >>> 15), 0x735a2d97) >>> 0
  return (h ^ (h >>> 15)) >>> 0
}

export function voiceFromSeed(seed: number, cfg: CommsConfig): Voice {
  const rng = createRng(voiceRngSeed(seed))
  const v = cfg.voice
  const female = nextFloat(rng) < v.femaleChance
  const vowels = Object.keys(cfg.vowels)
  const vowelWeights = vowels.map(() => 1 + v.vowelBias * nextFloat(rng) * nextFloat(rng))
  return {
    seed,
    female,
    pitch: inRange(rng, female ? v.pitchFemale : v.pitchMale),
    tract: inRange(rng, female ? v.tractFemale : v.tractMale),
    rate: inRange(rng, v.rate),
    breath: inRange(rng, v.breath),
    jitter: inRange(rng, v.jitter),
    range: inRange(rng, v.pitchRange),
    tilt: inRange(rng, v.tilt),
    vowelWeights,
  }
}
